import { type AnchorHTMLAttributes } from "react";
import styled, { css } from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FileText } from "lucide-react";
import { ChatMessageRole } from "@KassaroAB/shared/types/enums/ChatMessageRole";
import type { ChatMessageAttachment } from "@KassaroAB/shared/types/chatSession";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const Row = styled.div<{ $align: "start" | "end" }>`
  display: flex;
  justify-content: ${({ $align }) => ($align === "end" ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div<{ $role: "user" | "assistant" }>`
  max-width: min(100%, 640px);
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-radius: ${borderRadius.regular}px;
  font-size: ${fontSizes.regular}px;
  line-height: 1.45;
  word-break: break-word;
  background-color: ${({ $role }) =>
    $role === "user" ? colors.primary : colors.backgroundDark};
  color: ${({ $role }) =>
    $role === "user" ? colors.primaryContrastText : colors.text};
  border: ${({ $role }) =>
    $role === "assistant" ? `1px solid ${colors.border}` : "none"};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
`;

const AttachThumb = styled.img`
  width: 160px;
  height: 120px;
  object-fit: cover;
  border-radius: ${borderRadius.small}px;
  display: block;
`;

const FilePill = styled.a<{ $role: "user" | "assistant" }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.small}px;
  font-size: ${fontSizes.small}px;
  text-decoration: none;
  border: 1px solid
    ${({ $role }) => ($role === "user" ? "rgba(255,255,255,0.35)" : colors.border)};
  color: ${({ $role }) =>
    $role === "user" ? "rgba(255,255,255,0.92)" : colors.text};
  &:hover {
    text-decoration: underline;
  }
`;

const MarkdownInBubble = styled.div<{ $role: "user" | "assistant" }>`
  & > *:first-child {
    margin-top: 0;
  }
  & > *:last-child {
    margin-bottom: 0;
  }
  & p {
    margin: 0.5em 0;
  }
  & h1,
  & h2,
  & h3 {
    margin: 0.6em 0 0.35em;
    font-weight: 600;
    line-height: 1.25;
  }
  & h1 {
    font-size: ${fontSizes.large}px;
  }
  & h2 {
    font-size: ${fontSizes.regular}px;
  }
  & h3 {
    font-size: ${fontSizes.small}px;
  }
  & ul,
  & ol {
    margin: 0.5em 0;
    padding-left: 1.35em;
  }
  & li {
    margin: 0.2em 0;
  }
  & blockquote {
    margin: 0.5em 0;
    padding-left: ${spacing.x2}px;
    border-left: 3px solid
      ${({ $role }) => ($role === "user" ? "rgba(255,255,255,0.45)" : colors.border)};
    color: ${({ $role }) =>
      $role === "user" ? "rgba(255,255,255,0.92)" : colors.textMuted};
  }
  & hr {
    border: none;
    border-top: 1px solid
      ${({ $role }) => ($role === "user" ? "rgba(255,255,255,0.35)" : colors.border)};
    margin: ${spacing.x2}px 0;
  }
  & pre {
    margin: 0.5em 0;
    padding: ${spacing.x2}px;
    border-radius: ${borderRadius.small}px;
    overflow-x: auto;
    font-size: ${fontSizes.small}px;
    line-height: 1.4;
    ${({ $role }) =>
      $role === "user"
        ? css`
            background: rgba(0, 0, 0, 0.22);
          `
        : css`
            background: ${colors.background};
            border: 1px solid ${colors.border};
          `}
  }
  & code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9em;
  }
  & p code,
  & li code {
    padding: 0.1em 0.35em;
    border-radius: ${borderRadius.small}px;
    ${({ $role }) =>
      $role === "user"
        ? css`
            background: rgba(0, 0, 0, 0.22);
          `
        : css`
            background: ${colors.background};
            border: 1px solid ${colors.border};
          `}
  }
  & pre code {
    padding: 0;
    background: none;
    border: none;
    font-size: inherit;
  }
  & table {
    border-collapse: collapse;
    width: 100%;
    font-size: ${fontSizes.small}px;
    margin: 0.5em 0;
  }
  & th,
  & td {
    border: 1px solid
      ${({ $role }) => ($role === "user" ? "rgba(255,255,255,0.35)" : colors.border)};
    padding: ${spacing.x1}px ${spacing.x2}px;
    text-align: left;
  }
  & a {
    ${({ $role }) =>
      $role === "user"
        ? css`
            color: rgba(255, 255, 255, 0.95);
            text-decoration: underline;
          `
        : css`
            color: ${colors.primary};
            text-decoration: underline;
          `}
  }
`;

const markdownComponents = {
  a: ({
    href,
    children,
    ...props
  }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
};

type Props = {
  role: ChatMessageRole;
  content: string;
  attachments?: ChatMessageAttachment[];
};

export function ChatBubble({ role, content, attachments }: Props) {
  const displayRole = role === ChatMessageRole.USER ? "user" : "assistant";
  const align = role === ChatMessageRole.USER ? "end" : "start";

  return (
    <Row $align={align}>
      <Bubble $role={displayRole}>
        {attachments && attachments.length > 0 && (
          <AttachmentList>
            {attachments.map((att, i) =>
              att.contentType.startsWith("image/") ? (
                <a key={i} href={att.url} target="_blank" rel="noopener noreferrer">
                  <AttachThumb src={att.url} alt={att.filename} />
                </a>
              ) : (
                <FilePill
                  key={i}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  $role={displayRole}
                >
                  <FileText size={12} />
                  {att.filename}
                </FilePill>
              ),
            )}
          </AttachmentList>
        )}
        {content && (
          <MarkdownInBubble $role={displayRole}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {content}
            </ReactMarkdown>
          </MarkdownInBubble>
        )}
      </Bubble>
    </Row>
  );
}
